import {Box, BoxProps, Chip, Typography, useTheme} from "@mui/material"
import React, {FC} from "react"
import {makeStyles} from "tss-react/mui";
import {Link} from "react-router-dom";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import { isNotOverdue } from "@/app2/pages/HomePage/TodayLearningSection/TodayLearningCard";
import type {EnrolledCourseDto} from "@/app2/shared/data";

interface IProps extends BoxProps {
	course: EnrolledCourseDto
}

const formatDuration = (durationInSeconds: number): string =>
	new Date(durationInSeconds * 1000).toISOString().slice(14, 19);

const useStyles = makeStyles<{isOverdue: boolean}>()((theme, {isOverdue}) => ({
	backgroundImage: {
		width: "100%",
		height: "260px",
		zIndex: 1
	},
	itemContent: {
		width: "300px",
		height: "396px",
		background: "#141414",
		flex: "0 0 auto",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		boxShadow: theme.shadows[2],
		margin: 4,
		position: "relative",
		cursor: "pointer",
		transition: "all 0.3s ease",
		borderRadius: "24px",
		borderWidth: "1px",
		borderStyle: "solid",
		"&:hover": {
			borderColor: isOverdue ? theme.palette.error.main : theme.palette.primary.main,
			transform: "translateY(-4px)",
			boxShadow: theme.shadows[4],
			"& .hover-orange-overlay": {
				opacity: 1
			}
		},
		"[class*='previewImageSvg']": {
			width: "32px!important",
			height: "32px",
			svg: {
				width: "44px",
				height: "44px",
				transform: "translate(-6px, -6px)",
				marginRight: "auto",
				marginLeft: "auto"
			}
		}
	},
	title: {
		marginBottom: "0",
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "-webkit-box",
		WebkitLineClamp: "2",
		WebkitBoxOrient: "vertical"
	},
	chip: {
		fontWeight: 600
	},
	blankPreview: {
		width: "calc(100% + 60px)",
		height: "220px",
		top: "0",
		position: "relative",
		left: "-30px",
		overflow: "hidden",
		backgroundColor: theme.palette.primary.main,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		svg: {
			fill: theme.palette.common.white,
			width: "38px",
			height: "38px"
		}
	},
	videoThumbnailStyle: {
		backgroundColor: "transparent",
		borderTopLeftRadius: "16px",
		borderTopRightRadius: "16px",
		overflow: "hidden",
		border: "none",
		width: "100%",
		lineHeight: "1em",
		height: "260px",
		":hover": {
			backgroundColor: "transparent",
			border: "none"
		}
	},
	cardContents: {
		display: "flex",
		flexFlow: "wrap",
		height: "calc(100% - 220px)",
		padding: "0px 16px",
		boxSizing: "border-box"
	},
	itemDetailsContainer: {
		margin: "5px 0 15px",
		border: "none",
		flexGrow: 3,
		display: "flex",
		gap: "10px",
		flexDirection: "column",
		"& h3": {
			[theme.breakpoints.down("sm")]: {
				fontSize: "19px"
			}
		}

	},
	itemLink: {
		display: "flex",
		flexFlow: "wrap",
		height: "100%"
	},
	durationChip: {
		color: palette.grey100,
		backgroundColor: theme.palette.primary.main,
		"& .MuiChip-label": {
			fontSize: "11px"
		}
	},
	dueOnChip: {
		color:  palette.white,
		backgroundColor: palette.grey16,
		"& .MuiChip-label": {
			fontSize: "11px"
		}
	}
}));


export const EnrolledCourseCard:FC<IProps> = ({className, course}) => {
	const dueDate = typeof course.dueOn === "string" ? new Date(course.dueOn as string) : course.dueOn as Date;
	const {classes, cx} = useStyles({isOverdue: !isNotOverdue(dueDate)});
	const theme = useTheme();
	const {t} = useSSTranslation();

	const featuredImage = course.featuredImage ?? "/holding_image.jpg";
	return (
		<Box
			borderColor={isNotOverdue(dueDate) ? "rgba(161, 161, 160, 0.15)" : theme.palette.error.main}
			className={cx(classes.itemContent, className)}
			data-test-id="training-feed-module-card"
			role="article"
			aria-label={`Training module: ${course.title}`}
		>
			<Link
				className={classes.itemLink}
				to={"/course/" + course.slug}
				data-test-id="training-feed-module-link"
				aria-label={`Open ${course.title}`}
			>
				<Box
					className={classes.videoThumbnailStyle}
					style={{
						background: `linear-gradient(180deg, rgba(0,0,0,0) 50%, #141414 100%), url(${featuredImage}) lightgray 50% / cover no-repeat`

					}}
				/>
				<Box className={classes.cardContents}>
					<Box className={classes.itemDetailsContainer}>
						<Box gap={1} display="flex">
							<Chip
								icon={<AutorenewIcon style={{color: palette.grey100}}/>}
								className={classes.durationChip}
								label={formatDuration(course.remainingDurationSeconds)}
								size="small"
							/>
							{course.dueOn &&
								<Chip
									icon={<CalendarTodayOutlined style={{color: palette.white, fontSize: 15}}/>}
									className={classes.dueOnChip}
									style={{backgroundColor: isNotOverdue(dueDate) ? palette.grey16 : theme.palette.error.main}}
									label={t("common_due_on", "Due on") + " " + new Date(course.dueOn).toLocaleDateString()}
									size="small"
								/>}
						</Box>
						<Typography
							className={classes.title}
							variant="h4"
							data-test-id="training-feed-module-title"
							aria-level={4}
						>
							{course.title}
						</Typography>
						{course.enrollmentType && (
							<Typography
								variant="body2"
								component="div"
								align="left"
								color={palette.grey70}
							>
								{t(`user_enrollment_type_${course.enrollmentType}` as any, course.enrollmentType)}
							</Typography>
						)}
					</Box>
				</Box>
			</Link>
		</Box>
	)
}